/**
 * Runtime accent colour resolution for canvas / WebGL.
 *
 * CSS `var(--accent-h)` can't be used inside a 2D-canvas fillStyle string
 * (well, oklch() can in modern Chrome — see accentOklch) nor by three.js
 * (its Color parser doesn't understand oklch — see accentRgb). These helpers
 * read the live accent hue off <html> so canvas/WebGL draws follow the theme.
 *
 * Components should also depend on the themeStore accent so they REDRAW when
 * the user switches themes (the values here are read fresh each call).
 */

/** Read a live hue custom property off <html> (fallback 322). */
function hueVar(name: string): number {
  if (typeof document === 'undefined') return 322;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 322;
}

/** Read a numeric custom property off <html> with a fallback (default 1). */
function numVar(name: string, fallback = 1): number {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Current accent hue (--accent-h). */
export function accentHue(): number { return hueVar('--accent-h'); }
/** Current tint (background) hue (--tint-h). */
export function tintHue(): number { return hueVar('--tint-h'); }
/** Live chroma multipliers (0 = white/grayscale theme preset). */
export function accentChroma(): number { return numVar('--accent-c', 1); }
export function tintChroma(): number { return numVar('--tint-c', 1); }

/** An oklch accent colour string at the live accent hue (canvas-2D-safe). */
export function accentOklch(l = 0.70, c = 0.205, alpha = 1): string {
  const h = accentHue();
  const cc = c * accentChroma();
  return alpha >= 1 ? `oklch(${l} ${cc} ${h})` : `oklch(${l} ${cc} ${h} / ${alpha})`;
}
/** An oklch colour string at the live TINT hue (for backgrounds/waveform). */
export function tintOklch(l = 0.40, c = 0.10, alpha = 1): string {
  const h = tintHue();
  const cc = c * tintChroma();
  return alpha >= 1 ? `oklch(${l} ${cc} ${h})` : `oklch(${l} ${cc} ${h} / ${alpha})`;
}

let _probe: HTMLCanvasElement | null = null;
/** Resolve any CSS colour string (incl. oklch) to an { r, g, b } 0..1 triple
 *  via a 1px canvas — for three.js Color, which can't parse oklch. */
export function resolveRgb(css: string): { r: number; g: number; b: number } {
  if (typeof document === 'undefined') return { r: 1, g: 1, b: 1 };
  if (!_probe) { _probe = document.createElement('canvas'); _probe.width = 1; _probe.height = 1; }
  const ctx = _probe.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { r: 1, g: 1, b: 1 };
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = '#000000';
  ctx.fillStyle = css;        // invalid strings leave it at #000000
  ctx.fillRect(0, 0, 1, 1);
  const d = ctx.getImageData(0, 0, 1, 1).data;
  return { r: d[0] / 255, g: d[1] / 255, b: d[2] / 255 };
}

/** Accent as an { r, g, b } 0..1 triple at the given lightness / chroma. */
export function accentRgb(l = 0.70, c = 0.205): { r: number; g: number; b: number } {
  return resolveRgb(accentOklch(l, c, 1));
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }): string {
  const h = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** Accent as a `#rrggbb` hex at the given lightness / chroma — for three.js
 *  material colours (which can't parse oklch). */
export function accentHex(l = 0.70, c = 0.205): string {
  return rgbToHex(accentRgb(l, c));
}

/** Tint (background) as a `#rrggbb` hex at the given lightness / chroma. */
export function tintHex(l = 0.40, c = 0.10): string {
  return rgbToHex(resolveRgb(tintOklch(l, c, 1)));
}

function rgbaStr({ r, g, b }: { r: number; g: number; b: number }, a: number): string {
  return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
}

/** Accent as an `rgba()` string with alpha — for CANVAS (2D context can't read
 *  CSS vars, and oklch support in canvas is inconsistent). Resolves the live
 *  accent hue to concrete channels via the probe. */
export function accentRgba(l = 0.70, c = 0.205, a = 1): string {
  return rgbaStr(accentRgb(l, c), a);
}

/** Tint (background) as an `rgba()` string with alpha — canvas-safe. */
export function tintRgba(l = 0.40, c = 0.10, a = 1): string {
  return rgbaStr(resolveRgb(tintOklch(l, c, 1)), a);
}
